import React from "react";
import { fetchRepairDates, fetchPanelsForRepairDate } from "../../utils/firebase";
import { parseLocationString } from "../../utils/helperFunctions";
import "./tileStyleSheets/repairListTile.css";

import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import CheckIcon from "@mui/icons-material/Check";

export const REPAIR_DEFAULT_PROPS = {
    dateId: null,
    columns: {
        gridLocation: true,
        level: true,
        category: true,
        coords: true,
        notes: false,
        timestamp: false,
    },
    sortBy: "gridLocation",
    sortDir: "asc",
    _hydratedRows: null,
    margin: 12,
};

function toPrettyDate(id) {
    if (!id) return '';
    try {
        const mm = Number(id.slice(0, 2));
        const dd = Number(id.slice(2, 4));
        const yyyy = Number(id.slice(4));
        const d = new Date(Date.UTC(yyyy, mm - 1, dd));
        return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    } catch {
        return id;
    }
}

function idToUTC(id) {
    // id is "MMDDYYYY"
    if (!id || id.length !== 8) return NaN;
    const mm = Number(id.slice(0, 2));
    const dd = Number(id.slice(2, 4));
    const yyyy = Number(id.slice(4));
    return Date.UTC(yyyy, mm - 1, dd);
}

function startOfTodayUTC() {
    const now = new Date();
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

function pickNearestUpcomingDateId(dates) {
    if (!Array.isArray(dates) || dates.length === 0) return null;
    const today = startOfTodayUTC();

    const parsed = dates
        .map(d => ({ id: d, t: idToUTC(d) }))
        .filter(x => Number.isFinite(x.t))
        .sort((a, b) => a.t - b.t);

    const future = parsed.find(x => x.t >= today);
    if (future) return future.id;
    return parsed[parsed.length - 1]?.id ?? null;
}

function coerceLocString(panel) {
    if (typeof parseLocationString === "function") {
        const parsed = parseLocationString(panel.location || "");
        if (parsed) return `IB${parsed.ib}-G${parsed.group}-R${parsed.row}N-C${parsed.col}W`;
    }
    if (panel.location) return panel.location;
    const lat = Number(panel.latitude), lng = Number(panel.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return `Lat ${lat.toFixed(6)}, Lng ${lng.toFixed(6)}`;
    return "—";
}

function sortRows(rows, sortBy, sortDir) {
    const dir = sortDir === "desc" ? -1 : 1;
    const get = (r) => {
        switch (sortBy) {
            case "gridLocation": return coerceLocString(r);
            case "level": return r.level || "";
            case "category": return r.faultType || r.category || "";
            case "timestamp": {
                const t = r.timestamp?.seconds ? r.timestamp.seconds * 1000 : Date.parse(r.timestamp || "");
                return Number.isFinite(t) ? t : 0;
            }
            default: return coerceLocString(r);
        }
    };
    return [...(rows || [])].sort((a, b) => (get(a) < get(b) ? -1 * dir : get(a) > get(b) ? 1 * dir : 0));
}

function fmtCoords(lat, lng) {
    const a = Number(lat), b = Number(lng);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return "—";
    return `${a.toFixed(9)}, ${b.toFixed(9)}`;
}

function fmtTs(ts) {
    if (!ts) return "—";
    const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function useRepairDates(siteName) {
    const [dates, setDates] = React.useState([]);
    React.useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const d = await fetchRepairDates(siteName);
                if (alive) setDates(Array.isArray(d) ? d : []);
            } catch {
                if (alive) setDates([]);
            }
        })();
        return () => { alive = false; };
    }, [siteName]);
    return dates;
}

function useRepairRows(siteName, dateId, enabled) {
    const [rows, setRows] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    React.useEffect(() => {
        if (!enabled || !siteName || !dateId) { setRows(null); return; }

        const key = `${siteName}:${dateId}`;
        console.log('[RepairList] fetching panels for', key);

        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const panels = await fetchPanelsForRepairDate(siteName, dateId);
                if (alive) setRows(Array.isArray(panels) ? panels : []);
            } catch {
                if (alive) setRows([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [siteName, dateId, enabled]);
    return { rows, loading };
}


function extractBlockId(panel) {
    if (typeof parseLocationString === "function") {
        const parsed = parseLocationString(panel.location || "");
        if (parsed && parsed.ib != null) return `IB${parsed.ib}`;
    }
    const m = String(panel.location || "").match(/\bIB(\d+)\b/i);
    if (m) return `IB${Number(m[1])}`;
    return "Unknown";
}

export async function hydrateRepairList(siteName, dateId) {
    if (!siteName) return [];
    let useId = dateId;
    if(!useId) {
        const dates = await fetchRepairDates(siteName);
        useId = pickNearestUpcomingDateId(dates);
    }
    if (!useId) return [];
    const panels = await fetchPanelsForRepairDate(siteName, dateId);
    return Array.isArray(panels) ? panels : [];
}

export function RepairListSection({
    instanceId,
    props,
    siteName,
    exportMode = false,
}) {
    const p = { ...REPAIR_DEFAULT_PROPS, ...(props || {}) };

    const dates = useRepairDates(siteName);
    const effectiveDateId = React.useMemo(() => {
        if (p.dateId) return pickNearestUpcomingDateId;
        return pickNearestUpcomingDateId(dates);
    }, [p.dateid, dates]);
    const enabled = !!(siteName && effectiveDateId);

    const live = useRepairRows(siteName, effectiveDateId, enabled && !exportMode && !p._hydratedRows);
    const rows = (exportMode ? p._hydratedRows : (p._hydratedRows || live.rows)) || [];

    const levelsMap = p.levels || null;
    const categoriesMap = p.categories || null;
    const blocksMap = p.blocks || null;

    const includeByMap = (map, key) => (map ? map[key] !== false : true);

    const filtered = rows.filter((panel) => {
        const lvl = panel.level || "";
        const cat = panel.faultType || panel.category || "";
        const blk = extractBlockId(panel);
        return (
            includeByMap(levelsMap, lvl) &&
            includeByMap(categoriesMap, cat) &&
            includeByMap(blocksMap, blk)
        );
    });

    const sorted = sortRows(filtered, p.sortBy, p.sortDir);

    const show = (k) => p.columns?.[k];

    return (
        <div
            className="repairTile-root"
            style={{ marginBottom: p.margin }}
            data-block-kind="table"
            data-keep-thead="true"
            data-instance-id={instanceId}
        >
            <div className="repairTile-heading">
                <div className="repairTile-title">Repair List</div>
                <div className="repairTile-subtitle">
                    {effectiveDateId ? toPrettyDate(effectiveDateId) : "Select a repair date"}
                    {filtered.length ? ` • ${filtered.length} panels` : ""}
                </div>
            </div>

            <div className="repairTile-tableWrap">
                <table className="repairTile-table" data-splittable="rows">
                    <thead>
                        <tr>
                            {show("gridLocation") && <th>Grid Location</th>}
                            {show("level") && <th>Level</th>}
                            {show("category") && <th>Category</th>}
                            {show("coords") && <th>Coordinates</th>}
                            {show("notes") && <th>Notes</th>}
                            {show("timestamp") && <th>Timestamp</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((panel, i) => {
                            const loc = coerceLocString(panel);
                            const category = panel.faultType || panel.category || "—";
                            return (
                                <tr key={panel.panelId ?? panel.location ?? i}>
                                    {show("gridLocation") && <td>{loc}</td>}
                                    {show("level") && <td>{panel.level || "—"}</td>}
                                    {show("category") && <td>{category}</td>}
                                    {show("coords") && <td>{fmtCoords(panel.latitude, panel.longitude)}</td>}
                                    {show("notes") && <td>{panel.notes || "—"}</td>}
                                    {show("timestamp") && <td>{fmtTs(panel.timestamp)}</td>}
                                </tr>
                            );
                        })}
                        {!sorted.length && (
                            <tr><td colSpan={6} className="repairTile-empty">No panels in this list.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function FilterDropdown({
    title,
    options = [],
    selectionMap = null,
    onToggleItem,
    onSetAll,
    defaultOpen = false,
}) {
    const [open, setOpen] = React.useState(!!defaultOpen);
    const cardRef = React.useRef(null);

    // close on outside click / ESC
    React.useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (!cardRef.current) return;
            if (!cardRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const isOn = React.useCallback(
        (key) => (selectionMap ? selectionMap[key] !== false : true),
        [selectionMap]
    );
    const allChecked = options.length > 0 && options.every((k) => isOn(k));
    const noneChecked = options.length > 0 && options.every((k) => !isOn(k));
    const countOn = options.filter(isOn).length;

    return (
        <div ref={cardRef} className="repairTile-filterCard" data-open={open ? '1' : '0'}>
            <button
                type="button"
                className="repairTile-filterHead"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
            >
                <span className="repairTile-filterTitle">{title}</span>
                <span className="repairTile-filterMeta">{countOn}/{options.length}</span>
                <span className={`repairTile-caret ${open ? 'up' : 'down'}`} />
            </button>

            {/* Keep body absolutely positioned so it overlays (no layout shift) */}
            {open && (
                <div className="repairTile-filterBody" role="group" aria-label={`${title} filter`}>
                    <div className="repairTile-checkToolbar">
                        <label className="repairTile-check">
                            <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => {
                                    if (!el) return;
                                    el.indeterminate = !allChecked && !noneChecked;
                                }}
                                onChange={(e) => onSetAll(options, e.target.checked)}
                            />
                            <span>Select/Deselect All</span>
                        </label>
                    </div>

                    <div className="repairTile-checkList">
                        {options.map((opt) => (
                            <label key={opt} className="repairTile-check">
                                <input
                                    type="checkbox"
                                    checked={isOn(opt)}
                                    onChange={() => onToggleItem(opt)}
                                />
                                <span className="repairTile-ellipsis">{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export function RepairListEditPanel({
    instanceId,
    props,
    onChange,
    siteName,
    exportMode = false,
    onDone,
}) {
    const p = { ...REPAIR_DEFAULT_PROPS, ...(props || {}) };
    const set = (part) => onChange?.(part);

    const dates = useRepairDates(siteName);
    const enabled = !!(siteName && p.dateId);
    const { rows: liveRowsRaw } = useRepairRows(siteName, p.dateId, !!(siteName && p.dateId));
    const liveRows = Array.isArray(liveRowsRaw) ? liveRowsRaw : [];

    const uniqueLevels = React.useMemo(
        () => [...new Set(liveRows.map(r => r.level).filter(Boolean))].sort(),
        [liveRows]
    );
    const uniqueCategories = React.useMemo(
        () => [...new Set(liveRows.map(r => r.faultType || r.category).filter(Boolean))].sort(),
        [liveRows]
    );
    const uniqueBlocks = React.useMemo(
        () => [...new Set(liveRows.map(extractBlockId).filter(Boolean))].sort((a, b) => {
            const na = parseInt(a.replace(/\D/g, ''), 10) || 0;
            const nb = parseInt(b.replace(/\D/g, ''), 10) || 0;
            return na - nb || a.localeCompare(b);
        }),
        [liveRows]
    );

    const toggleInMap = (mapKey, key) => {
        const current = p[mapKey] || {};
        set({ [mapKey]: { ...current, [key]: !(current[key] !== false) } });
    };

    const setAllInMap = (mapKey, keys, value) => {
        if (!keys || keys.length === 0) return;
        const next = Object.fromEntries(keys.map(k => [k, !!value]));
        set({ [mapKey]: next });
    };

    return (
        <div className="repairTile-panel" onClick={(e) => e.stopPropagation()}>

            {/* Date */}
            <div className="repairTile-row">
                <label className="repairTile-label">Repair date</label>
                <select
                    className="repairTile-select"
                    value={p.dateId || ""}
                    onChange={(e) => set({ dateId: e.target.value || null, _hydratedRows: null })}
                >
                    <option value="">— Select a date —</option>
                    {dates.map((d) => (
                        <option key={d} value={d}>{toPrettyDate(d)}</option>
                    ))}
                </select>
            </div>
            

            {/* Columns */}
            <div className="repairTile-row">
                <label className="repairTile-label">Show Columns</label>
                <div className="repairTile-chipGrid" role="group" aria-label="Columns">
                    {Object.entries(p.columns).map(([k, v]) => {
                        const labelMap = {
                            gridLocation: "Grid Location",
                            level: "Level",
                            category: "Category",
                            coords: "Coordinates",
                            notes: "Notes",
                            timestamp: "Timestamp",
                        };
                        return (
                            <button
                                key={k}
                                type="button"
                                className={`repairTile-chip ${v ? "on" : ""}`}
                                aria-pressed={!!v}
                                onClick={() => set({ columns: { ...p.columns, [k]: !v } })}
                            >
                                {labelMap[k]}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Sort */}
            <div className="repairTile-row">
                <label className="repairTile-label">Sort By</label>
                <div className="repairTile-seg" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {["gridLocation", "level", "category", "timestamp"].map((key) => (
                            <button
                                key={key}
                                type="button"
                                className={`repairTile-segBtn ${p.sortBy === key ? "on" : ""}`}
                                aria-pressed={p.sortBy === key}
                                onClick={() => set({ sortBy: key })}
                            >
                                {key}
                            </button>
                        ))}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        {["asc", "desc"].map((dir) => (
                            <button
                                key={dir}
                                type="button"
                                className={`repairTile-segBtn ${p.sortDir === dir ? "on" : ""}`}
                                aria-pressed={p.sortDir === dir}
                                onClick={() => set({ sortDir: dir })}
                                title={dir === "asc" ? "Sort ascending" : "Sort descending"}
                            >
                                {dir === "asc" ? <ArrowUpwardIcon style={{ fontSize: 11 }} /> : <ArrowDownwardIcon style={{ fontSize: 11 }} />}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Filter */}
            <div className="repairTile-row" style={{ alignItems: 'start' }}>
                <label className="repairTile-label">Filter</label>

                <div className="repairTile-filterGrid">
                    <FilterDropdown
                        title="Level"
                        options={uniqueLevels}
                        selectionMap={p.levels}
                        onToggleItem={(key) => toggleInMap('levels', key)}
                        onSetAll={(keys, value) => setAllInMap('levels', keys, value)}
                    />
                    <FilterDropdown
                        title="Category"
                        options={uniqueCategories}
                        selectionMap={p.categories}
                        onToggleItem={(key) => toggleInMap('categories', key)}
                        onSetAll={(keys, value) => setAllInMap('categories', keys, value)}
                    />
                    <FilterDropdown
                        title="Block"
                        options={uniqueBlocks}
                        selectionMap={p.blocks}
                        onToggleItem={(key) => toggleInMap('blocks', key)}
                        onSetAll={(keys, value) => setAllInMap('blocks', keys, value)}
                    />
                </div>
            </div>

            {/* Bottom margin */}
            <div className="repairTile-row">
                <label className="repairTile-label">Bottom margin</label>
                <div className="repairTile-rangeWrap">
                    <input
                        type="range" min="0" max="48" step="1"
                        value={Number(p.margin ?? 12)}
                        onChange={(e) => set({ margin: Number(e.target.value) })}
                        className="repairTile-range"
                    />
                    <span className="repairTile-mono">{p.margin}px</span>
                </div>
            </div>

            <div className="repairTile-actions">
                <button
                    type="button"
                    className="btn save"
                    onClick={() => onDone?.()}
                    title="Done"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 10px',
                        borderRadius: 8,
                        border: '1px solid #1d6a2e',
                        background: '#2e7d32',
                        color: '#fffbed',
                        cursor: 'pointer',
                        fontWeight: 600,
                    }}
                >
                    <CheckIcon fontSize="small" />
                    Done
                </button>
            </div>
        </div>
    );
}